import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Eye, Snowflake, Send, ArrowDownToLine, Wallet, Activity, ChevronLeft, ChevronRight, ShieldHalf, Infinity as InfinityIcon, RotateCcw, Download, Lock, BookUser, Radio, ArrowLeftRight, Inbox, Check, X, PowerOff, CreditCard, KeyRound } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Nav from "@/components/Nav";
import VirtualCryptoCard from "@/components/VirtualCryptoCard";
import { CardConstitution } from "@/components/CardConstitution";
import IssueCardDialog from "@/components/IssueCardDialog";
import SignatureRevealDialog from "@/components/SignatureRevealDialog";
import PaymentDialog from "@/components/PaymentDialog";
import TopupDialog from "@/components/TopupDialog";
import SealProofDialog from "@/components/SealProofDialog";
import TransferDialog from "@/components/TransferDialog";
import RequestCardDialog from "@/components/RequestCardDialog";
import ChangeEmailDialog from "@/components/ChangeEmailDialog";
import { MerchantSection } from "@/components/MerchantSection";
import QrPayDialog from "@/components/QrPayDialog";
import QrTakeDialog from "@/components/QrTakeDialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [idx, setIdx] = useState(0);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [issueOpen, setIssueOpen] = useState(false);
  const [revealOpen, setRevealOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [qrPayOpen, setQrPayOpen] = useState(false);
  const [qrTakeOpen, setQrTakeOpen] = useState(false);
  const [takenPayload, setTakenPayload] = useState(null);
  const [topupOpen, setTopupOpen] = useState(false);
  const [sealOpen, setSealOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [changeEmailOpen, setChangeEmailOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [incoming, setIncoming] = useState([]);

  const isAdmin = user?.role === "admin";
  const walletCards = cards.filter((c) => ["active", "pending_release"].includes(c.status || "active"));
  const pendingCards = cards.filter((c) => (c.status || "active") === "pending");
  const card = walletCards[idx];

  const loadCards = useCallback(async () => {
    try {
      const res = await api.get("/cards");
      setCards(res.data);
      const wallet = res.data.filter((c) => ["active", "pending_release"].includes(c.status || "active"));
      setIdx((i) => Math.min(i, Math.max(0, wallet.length - 1)));
    } catch (e) {
      toast.error("Failed to load cards");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTxns = useCallback(async (cardId) => {
    if (!cardId) return setTxns([]);
    try {
      const res = await api.get(`/transactions?card_id=${cardId}`);
      setTxns(res.data);
    } catch (e) {}
  }, []);

  const loadIncoming = useCallback(async () => {
    try {
      const res = await api.get("/transfers/incoming");
      setIncoming(res.data);
    } catch (e) {}
  }, []);

  useEffect(() => {
    loadCards();
    loadIncoming();
  }, [loadCards, loadIncoming]);

  useEffect(() => {
    if (isAdmin) {
      api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    }
  }, [isAdmin]);

  useEffect(() => {
    if (card) loadTxns(card.id);
  }, [card, loadTxns]);

  const handleFreeze = async () => {
    try {
      const res = await api.post(`/cards/${card.id}/freeze`);
      setCards((cs) => cs.map((c) => (c.id === card.id ? res.data : c)));
      toast.success(res.data.frozen ? "Card frozen" : "Card unfrozen");
    } catch (e) {
      toast.error("Action failed");
    }
  };

  const handleConnect = async () => {
    try {
      const res = await api.post(`/cards/${card.id}/online?action=connect`);
      setCards((cs) => cs.map((c) => (c.id === card.id ? res.data : c)));
      toast.success(`Card online · ${res.data.online_count} active`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const handleDisconnect = async () => {
    try {
      const res = await api.post(`/cards/${card.id}/online?action=offline`);
      setCards((cs) => cs.map((c) => (c.id === card.id ? res.data : c)));
      toast.success("Card is now offline");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const confirmTransfer = async (t) => {
    try {
      const res = await api.post(`/transfers/${t.id}/confirm`);
      toast.success(`Confirmed · +${res.data.points_awarded} points (total ${res.data.total_points})`);
      loadIncoming();
      loadCards();
      checkAuth();
      window.dispatchEvent(new Event("signet-notify"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Confirm failed");
    }
  };

  const onTransferred = () => {
    loadCards();
  };

  const handleStatement = async () => {    try {
      const res = await api.get(`/cards/${card.id}/statement`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `SIGNET_statement_${card.card_number.slice(-4)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Statement downloaded");
    } catch (e) {
      toast.error("Failed to download statement");
    }
  };

  const handleTopup = () => setTopupOpen(true);

  const onTopupDone = () => {
    window.dispatchEvent(new Event("signet-notify"));
  };

  const onSealed = () => {
    setCards((cs) => cs.map((c) => (c.id === card.id ? { ...c, signatures_issued: 0 } : c)));
    loadTxns(card.id);
  };

  const onPayDone = (newBalance) => {
    setCards((cs) => cs.map((c) => (c.id === card.id ? { ...c, balance: newBalance, signatures_issued: (c.signatures_issued ?? 0) + 1, total_uses: (c.total_uses ?? 0) + 1 } : c)));
    loadTxns(card.id);
  };

  const acceptCard = async (c) => {
    try {
      await api.post(`/cards/${c.id}/accept`);
      toast.success("Card confirmed & registered");
      loadCards();
      window.dispatchEvent(new Event("signet-notify"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const declineCard = async (c) => {
    try {
      await api.post(`/cards/${c.id}/decline`);
      toast.success("Card declined");
      loadCards();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const requestRelease = async () => {
    try {
      await api.post(`/cards/${card.id}/release-request`);
      toast.success("Release requested — awaiting the other party's confirmation");
      loadCards();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const confirmRelease = async () => {
    try {
      await api.post(`/cards/${card.id}/release-confirm`);
      toast.success("Card released");
      loadCards();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const cancelRelease = async () => {
    try {
      await api.post(`/cards/${card.id}/release-cancel`);
      toast.success("Release cancelled");
      loadCards();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const requestModerator = async () => {
    try {
      await api.post("/moderator-requests", { note: "" });
      toast.success("Moderator request sent to the admin");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] relative">
      <div className="pointer-events-none fixed top-0 right-0 w-[600px] h-[600px] rounded-full bg-[#7A5CFF] opacity-[0.08] blur-[140px]" />
      <Nav active="wallet" />

      <main className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 py-8">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="font-mono-sig text-[11px] tracking-[0.3em] uppercase text-[#52525B] mb-1">Cardholder Wallet</p>
            <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter">
              Welcome, {user?.name?.split(" ")[0]}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {isAdmin && (
              <Button
                data-testid="goto-admin-button"
                variant="outline"
                onClick={() => navigate("/admin")}
                className="rounded-full border-white/10 bg-white/5 text-white hover:bg-white/10 h-11"
              >
                <ShieldHalf className="w-4 h-4 mr-2" /> Admin
              </Button>
            )}
            <Button
              data-testid="directory-button"
              variant="outline"
              onClick={() => navigate("/directory")}
              className="rounded-full border-white/10 bg-white/5 text-white hover:bg-white/10 h-11"
            >
              <BookUser className="w-4 h-4 mr-2" /> Directory
            </Button>
            <Button
              data-testid="request-card-button"
              variant="outline"
              onClick={() => setRequestOpen(true)}
              className="rounded-full border-white/10 bg-white/5 text-white hover:bg-white/10 h-11"
            >
              <CreditCard className="w-4 h-4 mr-2" /> Request Card
            </Button>
            {user?.role === "user" && (
              <Button
                data-testid="request-moderator-button"
                variant="outline"
                onClick={requestModerator}
                className="rounded-full border-[#7A5CFF]/40 bg-[#7A5CFF]/10 text-[#7A5CFF] hover:bg-[#7A5CFF]/20 h-11"
              >
                <ShieldHalf className="w-4 h-4 mr-2" /> Request Moderator
              </Button>
            )}
            {isAdmin && (
              <Button
                data-testid="issue-card-button"
                onClick={() => setIssueOpen(true)}
                className="rounded-full bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 font-semibold h-11"
              >
                <Plus className="w-4 h-4 mr-2" /> Issue Card
              </Button>
            )}
          </div>
        </div>

        {pendingCards.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="pending-cards"
            className="mb-6 rounded-2xl border border-[#00F0FF]/30 bg-[#00F0FF]/[0.06] p-5"
          >
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="w-4 h-4 text-[#00F0FF]" />
              <span className="font-mono-sig text-[10px] tracking-[0.3em] uppercase text-[#A0A0AB]">
                Cards issued to you · confirm to activate &amp; register
              </span>
            </div>
            <div className="space-y-3">
              {pendingCards.map((c) => (
                <div key={c.id} data-testid={`pending-card-${c.id}`} className="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/5 px-4 py-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="font-mono-sig text-[#00F0FF] text-sm">{c.nickname} · {c.cardholder_name}</p>
                    <p className="text-xs text-[#A0A0AB]">issued by {c.issued_by} · {(c.card_number || "").replace(/(.{4})/g, "$1 ").trim()}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      data-testid={`accept-card-${c.id}`}
                      size="sm"
                      onClick={() => acceptCard(c)}
                      className="rounded-full bg-[#00FF9D] text-black hover:bg-[#00FF9D]/90 h-9 font-semibold"
                    >
                      <Check className="w-4 h-4 mr-1.5" /> Confirm
                    </Button>
                    <Button
                      data-testid={`decline-card-${c.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => declineCard(c)}
                      className="rounded-full border-[#FF007F]/40 bg-[#FF007F]/10 text-[#FF007F] hover:bg-[#FF007F]/20 h-9"
                    >
                      <X className="w-4 h-4 mr-1.5" /> Decline
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        <MerchantSection cards={walletCards} />

        {incoming.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="incoming-transfers"
            className="mb-6 rounded-2xl border border-[#7A5CFF]/30 bg-[#7A5CFF]/[0.06] p-5"
          >
            <div className="flex items-center gap-2 mb-4">
              <Inbox className="w-4 h-4 text-[#7A5CFF]" />
              <span className="font-mono-sig text-[10px] tracking-[0.3em] uppercase text-[#A0A0AB]">
                Wired to you · confirm to earn points
              </span>
            </div>
            <div className="space-y-3">
              {incoming.map((t) => (
                <div key={t.id} className="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/5 px-4 py-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="font-mono-sig text-[#00F0FF] text-sm">{t.nickname || "card"}</p>
                    <p className="text-xs text-[#A0A0AB]">wired from {t.from_email} · +10 pts on confirm</p>
                  </div>
                  <Button
                    data-testid={`confirm-transfer-${t.id}`}
                    size="sm"
                    onClick={() => confirmTransfer(t)}
                    className="rounded-full bg-[#00FF9D] text-black hover:bg-[#00FF9D]/90 h-9 font-semibold"
                  >
                    <Check className="w-4 h-4 mr-1.5" /> Confirm
                  </Button>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {loading ? (
          <div className="h-64 flex items-center justify-center">
            <div className="w-10 h-10 rounded-full border-2 border-[#00F0FF]/20 border-t-[#00F0FF] animate-spin" />
          </div>
        ) : walletCards.length === 0 ? (
          <EmptyState isAdmin={isAdmin} onIssue={() => setIssueOpen(true)} onRequest={() => setRequestOpen(true)} />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Card + controls */}
            <div className="lg:col-span-3 space-y-6">
              <div className="relative">
                <div className="absolute -inset-6 bg-[#00F0FF] opacity-[0.12] blur-3xl rounded-full" />
                <AnimatePresence mode="wait">
                  <motion.div
                    key={card.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.4 }}
                  >
                    <VirtualCryptoCard card={card} />
                  </motion.div>
                </AnimatePresence>
              </div>

              {walletCards.length > 1 && (
                <div className="flex items-center justify-center gap-4">
                  <button
                    data-testid="prev-card-button"
                    onClick={() => setIdx((i) => (i - 1 + walletCards.length) % walletCards.length)}
                    className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="font-mono-sig text-xs text-[#A0A0AB]">
                    {idx + 1} / {walletCards.length}
                  </span>
                  <button
                    data-testid="next-card-button"
                    onClick={() => setIdx((i) => (i + 1) % walletCards.length)}
                    className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              )}

              {card.status === "pending_release" && (
                <div data-testid="release-banner" className="rounded-2xl border border-[#FF007F]/30 bg-[#FF007F]/[0.06] px-5 py-4">
                  {card.release_requested_by === "issuer" ? (
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <p className="text-sm text-white">An issuer requested to release this card. Confirm to release it.</p>
                      <div className="flex gap-2">
                        <Button data-testid="release-confirm-button" size="sm" onClick={confirmRelease} className="rounded-full bg-[#FF007F] text-white hover:bg-[#FF007F]/90 h-9 font-semibold">Confirm release</Button>
                        <Button data-testid="release-cancel-button" size="sm" variant="outline" onClick={cancelRelease} className="rounded-full border-white/20 bg-white/5 text-white hover:bg-white/10 h-9">Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <p className="text-sm text-white">Release requested — awaiting an issuer's confirmation.</p>
                      <Button data-testid="release-cancel-button" size="sm" variant="outline" onClick={cancelRelease} className="rounded-full border-white/20 bg-white/5 text-white hover:bg-white/10 h-9">Cancel request</Button>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <ActionButton testid="action-pay" icon={Send} label="Pay" color="#FF007F" onClick={() => setPayOpen(true)} />
                <ActionButton testid="action-qrpay" icon={KeyRound} label="Pay code" color="#00C2FF" onClick={() => { setTakenPayload(null); setQrPayOpen(true); }} />
                <ActionButton testid="action-qrtake" icon={KeyRound} label="Take" color="#FFE600" onClick={() => setQrTakeOpen(true)} />
                <ActionButton testid="action-reveal" icon={Eye} label="Reveal" color="#00F0FF" onClick={() => setRevealOpen(true)} />
                <ActionButton testid="action-topup" icon={ArrowDownToLine} label="Request top-up" color="#00FF9D" onClick={handleTopup} />
                <ActionButton testid="action-online" icon={Radio} label={`Go Online${(card.online_count ?? 0) > 0 ? ` · ${card.online_count}` : ""}`} color={card.online ? "#00FF9D" : "#52525B"} onClick={handleConnect} />
                <ActionButton testid="action-offline" icon={PowerOff} label="Go Offline" color={card.online ? "#FF007F" : "#52525B"} onClick={handleDisconnect} />
                <ActionButton testid="action-transfer" icon={ArrowLeftRight} label="Transfer" color="#7A5CFF" onClick={() => setTransferOpen(true)} />
              </div>

              <motion.button
                data-testid="action-seal"
                whileTap={{ scale: 0.97 }}
                whileHover={{ y: -2 }}
                onClick={() => setSealOpen(true)}
                className="w-full flex items-center justify-between rounded-2xl border border-[#FFE600]/30 bg-[#FFE600]/[0.06] px-5 py-4 hover:border-[#FFE600]/60 transition-colors"
              >
                <span className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#FFE600]/15 border border-[#FFE600]/40">
                    <InfinityIcon className="w-5 h-5 text-[#FFE600]" />
                  </span>
                  <span className="text-left">
                    <span className="block text-sm font-semibold text-white">Seal Last as Immortal</span>
                    <span className="block text-xs text-[#A0A0AB]">Archive proof · reset nonce to zero</span>
                  </span>
                </span>
                <RotateCcw className="w-4 h-4 text-[#FFE600]" />
              </motion.button>

              {card.status === "active" && (
                <div
                  data-testid="admin-lock-note"
                  className="w-full flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-3"
                >
                  <Lock className="w-4 h-4 text-[#00C2FF] shrink-0" />
                  <span className="text-xs text-[#A0A0AB]">
                    Freezing and removing this card is controlled by the network administrator (back-side lock).
                  </span>
                </div>
              )}

              <CardConstitution card={card} />
            </div>

            {/* Bento side */}
            <div className="lg:col-span-2 space-y-6">
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-xl">
                <div className="flex items-center gap-2 text-[#A0A0AB] mb-2">
                  <Wallet className="w-4 h-4" />
                  <span className="font-mono-sig text-[10px] tracking-[0.3em] uppercase">Available Balance</span>
                </div>
                <p data-testid="card-balance" className="font-display font-black text-4xl tracking-tighter">
                  {card.currency === "EUR" ? "\u20ac" : "$"}{card.balance.toFixed(2)}
                </p>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-[#52525B] text-sm">{card.cardholder_name} · {card.network}</p>
                  <span data-testid="signatures-issued" className="font-mono-sig text-[10px] tracking-wider text-[#00F0FF] uppercase">
                    {card.signatures_issued ?? 0} signatures
                  </span>
                </div>
                {card.credit_limit > 0 && (
                  <div data-testid="card-credit-panel" className="mt-4 rounded-xl border border-[#7A5CFF]/25 bg-[#7A5CFF]/[0.06] p-3">
                    <div className="flex items-center justify-between">
                      <span className="font-mono-sig text-[9px] tracking-[0.25em] uppercase text-[#B9A7FF]">Credit line</span>
                      <span className="font-mono-sig text-[9px] tracking-[0.2em] uppercase text-[#B9A7FF]">
                        limit {card.currency === "EUR" ? "\u20ac" : "$"}{card.credit_limit.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-baseline justify-between mt-1.5">
                      <div>
                        <p className="font-mono-sig text-[9px] tracking-wider uppercase text-[#52525B]">Available credit</p>
                        <p data-testid="card-available-credit" className="font-display font-black text-xl text-[#B9A7FF]">
                          {card.currency === "EUR" ? "\u20ac" : "$"}{(card.available_credit ?? 0).toFixed(2)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono-sig text-[9px] tracking-wider uppercase text-[#52525B]">Owed</p>
                        <p data-testid="card-credit-owed" className="font-mono-sig text-sm text-[#FF007F]">
                          {card.currency === "EUR" ? "\u20ac" : "$"}{(card.credit_owed ?? 0).toFixed(2)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono-sig text-[9px] tracking-wider uppercase text-[#52525B]">Spendable</p>
                        <p data-testid="card-spendable" className="font-mono-sig text-sm text-[#00FF9D]">
                          {card.currency === "EUR" ? "\u20ac" : "$"}{(card.spendable ?? card.balance).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2 mt-3">
                  <Lock className="w-3 h-3 text-[#52525B]" />
                  <span data-testid="internal-account" className="font-mono-sig text-[10px] tracking-wider text-[#A0A0AB]">
                    Internal acct {card.internal_account} · locked
                  </span>
                  <button
                    data-testid="change-email-link"
                    onClick={() => setChangeEmailOpen(true)}
                    className="font-mono-sig text-[10px] tracking-wider uppercase text-[#00F0FF] hover:text-[#00F0FF]/80"
                  >
                    change email
                  </button>
                </div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${card.online ? "bg-[#00FF9D] animate-pulse" : "bg-[#52525B]"}`} />
                    <span data-testid="card-online-status" className="font-mono-sig text-[10px] tracking-[0.2em] uppercase text-[#A0A0AB]">
                      {card.online ? `Online · ${card.online_count} active` : "Offline"}
                    </span>
                    {(card.online_count ?? 0) > 0 && (
                      <button
                        data-testid="disconnect-button"
                        onClick={handleDisconnect}
                        className="ml-1 font-mono-sig text-[9px] tracking-wider uppercase text-[#FF007F] hover:text-[#FF007F]/80"
                      >
                        disconnect
                      </button>
                    )}
                  </div>
                  <span data-testid="card-total-uses" className="font-mono-sig text-[10px] tracking-wider uppercase text-[#FFE600]">
                    {card.total_uses ?? 0} lifetime uses
                  </span>
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-xl">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2 text-[#A0A0AB]">
                    <Activity className="w-4 h-4" />
                    <span className="font-mono-sig text-[10px] tracking-[0.3em] uppercase">Recent Activity</span>
                  </div>
                  <button
                    data-testid="download-statement-button"
                    onClick={handleStatement}
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] font-medium text-[#A0A0AB] hover:text-white hover:bg-white/10 transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" /> Statement
                  </button>
                </div>
                {txns.length === 0 ? (
                  <p className="text-[#52525B] text-sm py-6 text-center">No transactions yet</p>
                ) : (
                  <div className="space-y-1 max-h-[320px] overflow-y-auto -mx-2 px-2" data-testid="transaction-list">
                    {txns.map((t) => (
                      <div key={t.id} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{t.merchant}</p>
                          <p className="font-mono-sig text-[10px] text-[#52525B]">
                            {new Date(t.created_at).toLocaleString()}
                          </p>
                        </div>
                        <span className={`font-mono-sig font-semibold text-sm shrink-0 ml-3 ${t.type === "credit" ? "text-[#00FF9D]" : "text-white"}`}>
                          {t.type === "credit" ? "+" : "−"}${t.amount.toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      <IssueCardDialog
        open={issueOpen}
        onOpenChange={setIssueOpen}
        users={users}
        onCreated={(c) => {
          if (c.owner_email === user?.email) {
            setCards((cs) => [...cs, c]);
            setIdx(cards.length);
          }
        }}
      />
      {card && <SignatureRevealDialog open={revealOpen} onOpenChange={setRevealOpen} card={card} />}
      {card && <PaymentDialog open={payOpen} onOpenChange={setPayOpen} card={card} onDone={onPayDone} />}
      {card && <QrPayDialog open={qrPayOpen} onOpenChange={setQrPayOpen} card={card} onDone={onPayDone} initialPayload={takenPayload} />}
      {card && <QrTakeDialog open={qrTakeOpen} onOpenChange={setQrTakeOpen} onUse={(p) => { setTakenPayload(p); setQrPayOpen(true); }} />}
      {card && <TopupDialog open={topupOpen} onOpenChange={setTopupOpen} card={card} onDone={onTopupDone} />}
      {card && <SealProofDialog open={sealOpen} onOpenChange={setSealOpen} card={card} onSealed={onSealed} />}
      {card && <TransferDialog open={transferOpen} onOpenChange={setTransferOpen} card={card} onTransferred={onTransferred} />}
      <RequestCardDialog open={requestOpen} onOpenChange={setRequestOpen} onRequested={() => window.dispatchEvent(new Event("signet-notify"))} />
      {card && <ChangeEmailDialog open={changeEmailOpen} onOpenChange={setChangeEmailOpen} card={card} onChanged={loadCards} />}
    </div>
  );
}

const ActionButton = ({ icon: Icon, label, color, onClick, testid }) => (
  <motion.button
    data-testid={testid}
    whileTap={{ scale: 0.94 }}
    whileHover={{ y: -3 }}
    onClick={onClick}
    className="flex flex-col items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.03] py-4 hover:border-white/20 transition-colors"
  >
    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}1a`, border: `1px solid ${color}40` }}>
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <span className="text-xs font-medium text-[#A0A0AB]">{label}</span>
  </motion.button>
);

const EmptyState = ({ onIssue, isAdmin, onRequest }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="rounded-3xl border border-dashed border-white/15 bg-white/[0.02] py-20 flex flex-col items-center text-center px-6"
  >
    <div className="w-16 h-16 rounded-2xl bg-[#00F0FF]/10 border border-[#00F0FF]/30 flex items-center justify-center mb-6">
      {isAdmin ? <Plus className="w-8 h-8 text-[#00F0FF]" /> : <Lock className="w-8 h-8 text-[#00F0FF]" />}
    </div>
    <h2 className="font-display font-black text-2xl tracking-tight mb-2">No cards yet</h2>
    {isAdmin ? (
      <>
        <p className="text-[#A0A0AB] max-w-sm mb-8">
          As the network issuer, mint a card for any account — each gets a unique 32-digit signature.
        </p>
        <Button
          data-testid="empty-issue-card-button"
          onClick={onIssue}
          className="rounded-full bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 font-semibold h-12 px-8"
        >
          <Plus className="w-4 h-4 mr-2" /> Issue a card
        </Button>
      </>
    ) : (
      <>
        <p className="text-[#A0A0AB] max-w-sm mb-8">
          Your account is active but cannot issue cards. Request one from the network issuer and it
          will appear here as soon as they mint it.
        </p>
        <Button
          data-testid="empty-request-card-button"
          onClick={onRequest}
          className="rounded-full bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 font-semibold h-12 px-8"
        >
          <CreditCard className="w-4 h-4 mr-2" /> Request a card
        </Button>
      </>
    )}
  </motion.div>
);
